import AddIcon from '@mui/icons-material/Add';
import { Autocomplete, Button, Stack, TextField } from '@mui/material';
import { useState } from 'react';

import { BUTTON_SX } from '~/theme';

/**
 * Height shared by the field and the button beside it. MUI's `small` sizes disagree —
 * a `small` outlined field is 40px and a `small` button ~31px — so the pair sat at two
 * different heights.
 */
const CONTROL_HEIGHT = 32;

export interface LinkOption {
  id: string;
  label: string;
  /** Already attached elsewhere — offered but not selectable. */
  disabled?: boolean;
}

interface LinkByNumberPickerProps {
  /** `undefined` while the options are still loading. */
  options: LinkOption[] | undefined;
  inputLabel: string;
  noOptionsText: string;
  onLink: (id: string) => Promise<void>;
}

/**
 * "Pick a number, press Link" — the add control for the Linked tabs, rendered as the
 * table's `headerContent` so it sits on the table's own surface. The sibling "all …for
 * serviced elevators" tabs are for looking, and put linking on the row itself.
 */
export function LinkByNumberPicker({
  options,
  inputLabel,
  noOptionsText,
  onLink,
}: LinkByNumberPickerProps) {
  const [selected, setSelected] = useState<LinkOption | null>(null);
  const [isLinking, setIsLinking] = useState(false);

  const handleLink = async () => {
    if (!selected) return;
    setIsLinking(true);
    try {
      await onLink(selected.id);
      // Only once the link is actually made. Clearing in `finally` meant a failed link
      // emptied the field too, so retrying started with finding the number again.
      setSelected(null);
    } catch {
      // `onLink` reports its own failures; swallowing here is what keeps this
      // click handler's promise from rejecting unhandled.
    } finally {
      setIsLinking(false);
    }
  };

  return (
    <Stack
      direction="row"
      spacing={1}
      alignItems="center"
      sx={{ width: { xs: '100%', sm: 'auto' } }}
    >
      <Autocomplete
        value={selected}
        onChange={(_event, option) => setSelected(option)}
        getOptionDisabled={(option) => option.disabled === true}
        noOptionsText={noOptionsText}
        loading={options === undefined}
        options={options ?? []}
        getOptionLabel={(option) => option.label}
        isOptionEqualToValue={(option, value) => option.id === value.id}
        renderInput={(props) => <TextField {...props} label={inputLabel} />}
        size="small"
        sx={{
          flex: { xs: 1, sm: '0 0 260px' },
          minWidth: 0,
          '& .MuiOutlinedInput-root': { height: CONTROL_HEIGHT, py: 0 },
          // The unshrunk label centres itself against MUI's 40px small field, so it needs
          // re-centring against this one.
          '& .MuiInputLabel-outlined:not(.MuiInputLabel-shrink)': {
            transform: 'translate(14px, 5px) scale(1)',
          },
        }}
      />
      <Button
        onClick={handleLink}
        loading={isLinking}
        color="primary"
        variant="outlined"
        size="small"
        startIcon={<AddIcon />}
        disabled={!selected}
        sx={{ ...BUTTON_SX, flexShrink: 0, height: CONTROL_HEIGHT }}
      >
        Link
      </Button>
    </Stack>
  );
}
